import { useMemo } from 'react'
import { useAppDispatch } from 'app/state'
import { AlertSeverity, NotificationActions, hideNotification, showNotification } from '../state/notificationSlice'

export interface NotificationOptions {
  actions?: NotificationActions[]
  timeout?: number
  error?: unknown
  closeable?: boolean
  autoWidth?: boolean
}

function useNotifications() {
  const dispatch = useAppDispatch()

  const sendErrorNotification = (message: string, options?: NotificationOptions) => {
    dispatch(showNotification({ msg: message, formatted: false, type: AlertSeverity.Error, ...options }))
  }

  const sendErrorNotificationLocalize = (labelId: string, options?: NotificationOptions) => {
    dispatch(showNotification({ msg: labelId, formatted: true, type: AlertSeverity.Error, ...options }))
  }

  const sendSuccessNotification = (message: string, options?: NotificationOptions) => {
    dispatch(showNotification({ msg: message, formatted: false, type: AlertSeverity.Success, ...options }))
  }

  const sendSuccessNotificationLocalize = (labelId: string, options?: NotificationOptions) => {
    dispatch(showNotification({ msg: labelId, formatted: true, type: AlertSeverity.Success, ...options }))
  }

  const sendInfoNotificationLocalize = (
    labelId: string,
    type: AlertSeverity = AlertSeverity.Info,
    options?: NotificationOptions
  ) => {
    dispatch(showNotification({ msg: labelId, formatted: true, type, ...options }))
  }

  const sendNeutralNotification = (message: string, options?: NotificationOptions) => {
    dispatch(showNotification({ msg: message, formatted: false, type: AlertSeverity.Neutral, ...options }))
  }

  const sendNeutralNotificationLocalize = (labelId: string, options?: NotificationOptions) => {
    dispatch(showNotification({ msg: labelId, formatted: true, type: AlertSeverity.Neutral, ...options }))
  }

  const sendWarningNotification = (message: string, options?: NotificationOptions) => {
    dispatch(showNotification({ msg: message, formatted: false, type: AlertSeverity.Warning, ...options }))
  }

  const sendWarningNotificationLocalize = (labelId: string, options?: NotificationOptions) => {
    dispatch(showNotification({ msg: labelId, formatted: true, type: AlertSeverity.Warning, ...options }))
  }

  const resetNotification = () => {
    dispatch(hideNotification())
  }

  return useMemo(
    () => ({
      sendErrorNotification,
      sendErrorNotificationLocalize,
      sendSuccessNotification,
      sendSuccessNotificationLocalize,
      sendInfoNotificationLocalize,
      sendNeutralNotification,
      sendNeutralNotificationLocalize,
      sendWarningNotification,
      sendWarningNotificationLocalize,
      resetNotification
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  )
}

export default useNotifications
